import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, Undo2, Loader2, Mail } from "lucide-react";

const INVALIDATE = ["roster", "roster-pending", "roster-sent", "cleaners", "roster-day", "roster-day-labels", "timeline", "cleans", "timesheets", "messages"];

export function RosterPending() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["roster-pending"], queryFn: async () => (await api.get("/roster/pending")).data, refetchInterval: 30000 });
  const done = (msg) => () => { INVALIDATE.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast.success(msg); };
  const send = useMutation({
    mutationFn: (ids) => api.post("/roster/pending/send", { cleaner_ids: ids }),
    onSuccess: (r) => done(t("Roster changes sent to {{n}} cleaner(s)", { n: r.data.cleaners }))(),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const undo = useMutation({
    mutationFn: (ids) => api.post("/roster/pending/undo", { cleaner_ids: ids }),
    onSuccess: done(t("Unsent roster changes were undone")),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  if (!data || data.count === 0) return null;
  const busy = send.isPending || undo.isPending;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs" data-testid="roster-pending">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-amber-900" data-testid="roster-pending-count">{t("Unsent roster changes")}: {data.count}</span>
        <span className="text-amber-800">{t("Cleaners have not been told yet. Save/send when you're happy, or undo to rewind.")}</span>
        <span className="ml-auto flex gap-1">
          <Button size="sm" className="h-7 gap-1 text-xs" disabled={busy} onClick={() => send.mutate(null)} data-testid="roster-send-all">
            {send.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {t("Save / send all")}
          </Button>
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={busy} onClick={() => window.confirm(t("Undo all unsent roster changes and put the roster back how it was?")) && undo.mutate(null)} data-testid="roster-undo-all">
            <Undo2 className="h-3.5 w-3.5" /> {t("Undo all")}
          </Button>
        </span>
      </div>
      <ul className="mt-2 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
        {data.groups.map((g) => (
          <li key={g.cleaner_id} className="rounded border border-amber-200 bg-white p-2" data-testid={`roster-pending-${g.cleaner_id}`}>
            <div className="flex items-center gap-1">
              <span className="font-semibold">{g.name}</span>
              {g.email && <Mail className="h-3 w-3 text-muted-foreground" title={g.email} />}
              <span className="ml-auto flex gap-0.5">
                <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" disabled={busy} onClick={() => send.mutate([g.cleaner_id])} data-testid={`roster-send-${g.cleaner_id}`}><Send className="h-3 w-3" /></Button>
                <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" disabled={busy} onClick={() => undo.mutate([g.cleaner_id])} data-testid={`roster-undo-${g.cleaner_id}`}><Undo2 className="h-3 w-3" /></Button>
              </span>
            </div>
            <ul className="mt-1 space-y-0.5 text-[11px]">
              {g.changes.map((c) => <li key={c.date} data-testid={`roster-change-${g.cleaner_id}-${c.date}`}>{c.line}</li>)}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
